// Action Creators
// Constants
import { CopilotFeatureName } from "@ms/uno-copilot/lib/local/constants/CopilotConstants";
import { EngagementTelemetryConstants } from "@ms/uno-constants/lib/local/EngagementTelemetryConstants";
import { IrisTriggerAction } from "@ms/uno-constants/lib/local/IrisConstants";
import { ElementIds, ViewElementIds } from "@ms/uno-constants/lib/local/UIConstants";
import { DefaultMyTasksViewPivotType, MyTasksViewPivotType } from "@ms/uno-constants/lib/local/ViewPivots";
import { TryPremiumEntryPoint, UpsellTrialButtonDisplayMode } from "@ms/uno-constants/lib/local/UpsellConstants";
import { SubRouteType, ViewType, SubRouteKeys } from "@ms/uno-constants/lib/local/RoutingConstants";
import { ComponentNames } from "../../../constants/ComponentNames";
import { FloodgateActivity } from "@ms/uno-office-feedback/lib/local/FloodgateConstants";
import { StoreEntityStatus } from "@ms/uno-constants/lib/local/StoreConstants";
import { ViewName, ViewRenderState } from "@ms/uno-constants/lib/local/ViewConstants";
import { AssignedToUserTaskGroupingOptions, TaskGroupingState, AssignToUserTaskGroupingOptionsWithCopilot } from "@ms/uno-constants/lib/local/GroupingConstants";
import { CommandBarPlaceHolder } from "@ms/uno-controls/lib/local/controls/presentational/uxPlaceHolders/CommandBarPlaceHolder";
import { UnoFilterPivotHeight } from "@ms/uno-controls/lib/local/constants/StyleConstants";
// Controls
import { AssignedToUserGridView } from "@ms/uno-controls/lib/local/controls/container/views/myTasks/AssignedToUserGridView";
import { AllUserTasksGridView } from "@ms/uno-controls/lib/local/controls/container/views/myTasks/AllUserTasksGridView";
import { MyTasksBasicPlanTasksGridView } from "@ms/uno-controls/lib/local/controls/container/views/myTasks/MyTasksBasicPlanTasksGridView";
import { LazyMyTasksBoardView } from "@ms/uno-controls/lib/local/controls/container/views/myTasks/LazyMyTasksBoardView";
import { UserTasksViewHeader } from "../../presentational/userTasksViewHeader/UserTasksViewHeader";
import { PageLayout } from "@ms/uno-controls/lib/local/controls/presentational/pageLayout/PageLayout";
import { FilterPivotRow } from "@ms/uno-controls/lib/local/controls/presentational/filterPivotRow/FilterPivotRow";
import { ActionButton, DirectionalHint, Icon } from "@fluentui/react";
import { PageComponent } from "@ms/uno-controls/lib/local/controls/PageComponent";
import { LazyEmptyStateView } from "@ms/uno-controls/lib/local/controls/presentational/emptyStateView/EmptyStateViewLazyLoader";
import { EmptyStateViewType } from "@ms/uno-controls/lib/local/controls/presentational/emptyStateView/EmptyStateView";
import { getLazyTasksImage } from "@ms/uno-controls/lib/local/controls/presentational/image/ImageLoader";
import { LoadingIndicator } from "@ms/uno-controls/lib/local/controls/presentational/LoadingIndicator/LoadingIndicator";
// Models
import { Filter } from "@ms/uno-models/lib/local/client/filter/Filter";
import { TaskAccessContext } from "@ms/uno-models/lib/local/client/legacyPlanner/task/TaskAccessContext";
import { MyTasksFilterOptions } from "@ms/uno-models/lib/local/constants/FilterConstants";
import { Task } from "@ms/uno-models/lib/local/client/legacyPlanner/task/Task";
import { ModalType } from "@ms/uno-models/lib/local/client/modal/ModalType";
import { Store } from "@ms/uno-stores/lib/local/stores/Store";
// Styles
import { menuIconButtonStyles } from "@ms/uno-controls/lib/local/controls/presentational/filterPivotRow/FilterPivotRow.styles";
import { iconButtonStyles } from "@ms/uno-controls/lib/local/controls/presentational/pageHeaderLayout/PageHeaderLayout.styles";
import { getThemedContextualMenuStyles } from "@ms/uno-controls/lib/local/controls/contextualMenu/ContextualMenu.styles";
import { copilotIconProps, gridIconFilledProps, gridIconProps, myTasksRegularIconProps, taskboardIconFilledProps, taskboardIconProps } from "@ms/uno-controls/lib/local/icons/icons.styles";
// Utilities
import { extractMyTasksRoutingDetailsFromSubRoute, generateSubRouteFromRoutingDetailsForMyTasks } from "@ms/uno-routing";
import { getEnumTypeValueForKey } from "@ms/uno-routing/lib/local/utilities/RouteUtilities";
import { Strings, loadTranslations } from "../../../utilities/CultureUtilities";
import { getMyTasksPivotString } from "../../../utilities/StringUtilities";
import { CopilotTelemetryHandler } from "@ms/uno-copilot/lib/local/utilities/CopilotTelemetryHandler";
import { isStatusFetchingOrUnfetched, isStatusFailure } from "@ms/uno-stores/lib/local/utilities/StatusUtilities";
import { getViewKey } from "@ms/uno-utilities/lib/local/ViewUtilities";
import { shouldDisplayLoadingIndicator } from "@ms/uno-controls/lib/local/utilities//LoadingIndicatorUtilities";
import { copyToClipboard } from "@ms/uno-utilities/lib/local/ClipboardUtilities";
import isEmpty from "lodash/isEmpty";
import React from "react";
/**
 * My Tasks top level smart component
 */ export class MyTasksPage extends PageComponent {
    getStoresToSubscribeTo() {
        // Return the array of stores that this component needs to subscribe to
        return [
            Store.AppContext,
            Store.BasicPlan,
            Store.BasicTask,
            Store.BasicPermissions,
            Store.Modal,
            Store.Route,
            Store.Tenant,
            Store.Upsell,
            Store.View,
            Store.PremiumPlanPermissions,
            Store.PerformanceCollection
        ];
    }
    async componentDidMount() {
        const { configProvider, storeLoader } = this.props;
        const routeStore = storeLoader.load(Store.Route);
        const myTasksRoutingDetails = extractMyTasksRoutingDetailsFromSubRoute(routeStore.getCurrentSubRoute(), routeStore.getCurrentSubRouteType());
        const currentFilterPivot = myTasksRoutingDetails?.pivotType ?? DefaultMyTasksViewPivotType;
        this.setState({
            currentFilterPivot
        });
        if (configProvider().flights.EnableTaskEditorRouting && !configProvider().flights.EnableDataFetchViaWorker) {
            // We need to subscribe to route store so that if we navigate to a different task while on this page, we can fetch the new task details
            routeStore.subscribeToStore(this.routeChangeFetchDataCallback);
        }
        this.logFloodgateActivity(FloodgateActivity.MyTasks);
        if (!this.props.configProvider().flights.EnableCampaignTriggerFromWorker) {
            this.triggerIrisCampaignActions();
        }
        super.componentDidMount();
    }
    componentDidUpdate(prevProps, prevState) {
        const { configProvider, storeLoader } = this.props;
        const [routeStore, appContextStore] = storeLoader.loadAll([
            Store.Route,
            Store.AppContext
        ]);
        if (configProvider().flights.EnableTaskEditorRouting) {
            this.openTaskDetailsIfNeeded();
        }
        // For cases when the user is navigating to a non-ATM pivot but todo settings are not present, switch to ATM pivot
        if (isStatusFailure(appContextStore.getTodoSettings().status)) {
            this.ensurePageRoutedToAssignedToMe();
        }
        const myTasksRoutingDetails = extractMyTasksRoutingDetailsFromSubRoute(routeStore.getCurrentSubRoute(), routeStore.getCurrentSubRouteType());
        const currentFilterPivot = myTasksRoutingDetails?.pivotType ?? DefaultMyTasksViewPivotType;
        if (currentFilterPivot !== this.state.currentFilterPivot) {
            this.setState({
                currentFilterPivot
            });
        }
        super.componentDidUpdate(prevProps, prevState);
    }
    componentWillUnmount() {
        const { configProvider, storeLoader } = this.props;
        if (configProvider().flights.EnableTaskEditorRouting && !configProvider().flights.EnableDataFetchViaWorker) {
            const routeStore = storeLoader.load(Store.Route);
            routeStore.unsubscribeFromStore(this.routeChangeFetchDataCallback);
        }
        super.componentWillUnmount();
    }
    /**
     * Determine whether critical data needs to be re-fetched upon ComponentDidUpdate in the React Lifecycle
     * @param prevProps Previous control properties
     * @param prevState Previous control state
     */ fetchCriticalDataWhenComponentDidUpdate(prevProps, prevState) {
        return prevState.currentFilterPivot !== this.state.currentFilterPivot;
    }
    /**
     * Determine whether pageReadyActionDispatched needs to be reset upon ComponentDidUpdate in the React Lifecycle
     * @param prevProps Previous control properties
     * @param prevState Previous control state
     */ shouldResetPageReadyActionDispatched(prevProps, prevState) {
        return prevState.currentFilterPivot !== this.state.currentFilterPivot;
    }
    /**
     * Name of the component
     */ name() {
        return ComponentNames.MyTasksPage;
    }
    /**
     * Fetch critical data necessary for this component and its children
     * @param props Props for the component
     * @param state State of the component
     */ async fetchCriticalData(props, state) {
        const { configProvider } = props;
        if (!configProvider().flights.EnableDataFetchViaWorker) {
            const viewAC = await this.getViewAC();
            viewAC.fetchViewData(state.currentFilterPivot, true);
        }
    }
    /**
     * Fetch any non-critical data necessary for this component and its children
     * @param props Props for the component
     * @param state State of the component
     */ fetchNonCriticalData(props, state) {}
    /**
     * Method to check whether all critical data required for user interaction is available
     */ checkForCriticalDataReady() {
        const { currentFilterPivot } = this.state;
        let isReady = false;
        switch(currentFilterPivot){
            case MyTasksViewPivotType.PrivateTasks:
                isReady = this.isPrivateTasksViewDataReady();
                break;
            case MyTasksViewPivotType.All:
            case MyTasksViewPivotType.AssignedToMe:
                isReady = this.isAssignedToMeTasksViewDataReady();
                break;
            case MyTasksViewPivotType.FlaggedEmails:
                isReady = this.isFlaggedEmailsViewDataReady();
                break;
            default:
                return false;
        }
        if (isReady) {
            this.onPageCriticalDataFetchCompletion();
        }
        return isReady;
    }
    getRenderState() {
        let state = ViewRenderState.None;
        const { currentFilterPivot } = this.state;
        switch(currentFilterPivot){
            case MyTasksViewPivotType.PrivateTasks:
                state = this.getPrivateTasksViewRenderState();
                break;
            case MyTasksViewPivotType.All:
            case MyTasksViewPivotType.AssignedToMe:
                state = this.getAssignedToMeViewRenderState();
                break;
            case MyTasksViewPivotType.FlaggedEmails:
                state = this.getFlaggedEmailsViewRenderState();
                break;
            default:
                return ViewRenderState.None;
        }
        if (state === ViewRenderState.Critical || state === ViewRenderState.All) {
            this.onPageCriticalDataFetchCompletion();
        }
        return state;
    }
    /**
     * Open task details if route contains a taskId
     */ async openTaskDetailsIfNeeded() {
        const { storeLoader } = this.props;
        const [routeStore, modalStore] = storeLoader.loadAll([
            Store.Route,
            Store.Modal
        ]);
        const subRoute = routeStore.getCurrentSubRoute();
        const routingDetails = extractMyTasksRoutingDetailsFromSubRoute(subRoute, routeStore.getCurrentSubRouteType());
        const taskId = routingDetails?.task ?? null;
        // Check if the task editor modal is already open for the task
        const modalDataLayers = modalStore?.getCurrentModalDataLayers() ?? [];
        const containsTaskEditorModal = modalDataLayers.some((modal)=>modal.modalType === ModalType.TaskEditor && modal["taskId"] && modal["taskId"] === taskId);
        if (taskId && !containsTaskEditorModal) {
            const { actionCreatorProviders } = this.props;
            const modalAC = await actionCreatorProviders.modal();
            // IMPORTANT: await'ing this call is necessary to ensure the task editor is opened quickly
            await modalAC.openTaskEditorModal(taskId, TaskAccessContext.AssignedToUser);
        }
    }
    /**
     * Ensure that the page is routed to AssignedToMe view
     */ ensurePageRoutedToAssignedToMe() {
        const { actionCreatorProviders, storeLoader } = this.props;
        const routeStore = storeLoader.load(Store.Route);
        const routingDetails = extractMyTasksRoutingDetailsFromSubRoute(routeStore.getCurrentSubRoute(), routeStore.getCurrentSubRouteType());
        if (routingDetails && routingDetails.pivotType !== MyTasksViewPivotType.AssignedToMe) {
            const routeActionCreator = actionCreatorProviders.routing();
            routeActionCreator.replaceSubRoute(generateSubRouteFromRoutingDetailsForMyTasks({
                pivotType: MyTasksViewPivotType.AssignedToMe,
                viewType: ViewType.Grid
            }));
        }
    }
    getPageTitle() {
        return Strings.MyTasksPage_Title;
    }
    renderComponent() {
        return /*#__PURE__*/ React.createElement(PageLayout, {
            pageId: ViewElementIds.MyTasksView,
            header: this.renderPageHeader(),
            commandBar: this.renderCommandBar(),
            view: this.renderView()
        });
    }
    renderPageHeader() {
        const { storeLoader, configProvider } = this.props;
        const [routeStore, appContextStore, upsellStore, tenantStore, performanceCollectionStore] = storeLoader.loadAll([
            Store.Route,
            Store.AppContext,
            Store.Upsell,
            Store.Tenant,
            Store.PerformanceCollection
        ]);
        const currentViewPivot = getEnumTypeValueForKey(routeStore?.getCurrentSubRoute() ?? [], SubRouteKeys.View, ViewType) ?? ViewType.Grid;
        const isFullFrameMode = appContextStore.isFullFrameMode();
        const upsellTrialButtonDisplayMode = isFullFrameMode ? UpsellTrialButtonDisplayMode.Hidden : upsellStore?.getUpsellTrialButtonDisplayMode() ?? UpsellTrialButtonDisplayMode.Hidden;
        const trialButtonProps = {
            displayMode: upsellTrialButtonDisplayMode,
            onClickTrialButton: this.onClickTrialStatusButton
        };
        const showMoreButton = !isFullFrameMode;
        const showFeedbackButton = tenantStore?.getCurrentTenantFeedbackSettings().data?.allowFeedback ?? false;
        const headerOverflowMenuProps = {
            showFeedbackButton,
            onClickFeedback: this.onClickFeedback,
            onClickAbout: this.onClickAbout,
            showCopySessionIdButton: configProvider().flights.EnableCopySessionId,
            onClickCopySessionId: this.onClickCopySessionId,
            enableExternalUrlRegexCheck: configProvider().flights.EnableExternalUrlRegexCheck
        };
        const iconElement = /*#__PURE__*/ React.createElement(Icon, myTasksRegularIconProps);
        const pivotsSectionProps = configProvider().flights.EnableMyTasksBoardView ? {
            pivotItems: this.getViewPivotItems(),
            overflowPivotItems: [],
            navigateToPivot: this.onViewPivotChange,
            currentPivot: currentViewPivot,
            showTooltip: true,
            enableUpsellOverflowMenuUpdate: configProvider().flights.EnableUpsellOverflowMenuUpdate
        } : undefined;
        return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(UserTasksViewHeader, {
            title: Strings.MyTasksPage_Title,
            iconElement: iconElement,
            pivotsSectionProps: pivotsSectionProps,
            trialButtonProps: trialButtonProps,
            showMoreButton: showMoreButton,
            headerOverflowMenuProps: headerOverflowMenuProps,
            logFloodgateActivity: this.logFloodgateActivity
        }, this.renderCopilotButton()), shouldDisplayLoadingIndicator(configProvider, performanceCollectionStore.getViewDataFetchState(ViewName.MyTasksPage)) && /*#__PURE__*/ React.createElement(LoadingIndicator, null));
    }
    renderCommandBar() {
        const { currentFilterPivot } = this.state;
        const { configProvider, storeLoader } = this.props;
        const [routeStore, appContextStore, viewStore] = storeLoader.loadAll([
            Store.Route,
            Store.AppContext,
            Store.View
        ]);
        // If filters are not present in store yet, return empty command bar
        if (viewStore == null) {
            return /*#__PURE__*/ React.createElement(CommandBarPlaceHolder, {
                commandBarHeight: UnoFilterPivotHeight
            });
        }
        const currentViewPivot = getEnumTypeValueForKey(routeStore?.getCurrentSubRoute() ?? [], SubRouteKeys.View, ViewType) ?? ViewType.Grid;
        // Pills should be shown only if the todo settings are present
        let pillPivotProps;
        if (appContextStore.getTodoSettings().status === StoreEntityStatus.Present) {
            pillPivotProps = {
                pivotProps: {
                    selectedKey: currentFilterPivot
                },
                pivotItemsProps: this.getFilterPivotItems(),
                onPivotItemSelected: this.onMyTasksPagePivotItemChanged
            };
        }
        const currentTaskFilter = viewStore?.getCurrentTaskFilter(this.getViewKey());
        const currentlyTypedText = currentTaskFilter?.filters?.find((filter)=>{
            return filter.field === "title";
        })?.values?.[0] ?? "";
        const overflowContextMenuProps = currentFilterPivot === MyTasksViewPivotType.AssignedToMe ? {
            items: [
                {
                    key: "iCalendar",
                    text: Strings.ICalendarDialog_AssignedToMeDropdownText,
                    onClick: ()=>{
                        this.openICalendarModal();
                    }
                }
            ],
            shouldFocusOnMount: false,
            directionalHint: DirectionalHint.bottomRightEdge,
            styles: getThemedContextualMenuStyles(this.props.theme)
        } : undefined;
        const showPMAInMyTasksBoardView = (configProvider().flights.EnableGsdInMeetingPlans || configProvider().flights.EnableGsdInBasicPlans) && configProvider().settings.GSDInBasicPlanSettings.enablePMAInMyDayAndMyTasksBoardView;
        const groupByMenuButtonProps = configProvider().flights.EnableMyTasksBoardView && currentViewPivot !== ViewType.Grid ? {
            taskGroupingOptions: showPMAInMyTasksBoardView ? AssignToUserTaskGroupingOptionsWithCopilot : AssignedToUserTaskGroupingOptions,
            currentTaskGrouping: viewStore?.getCurrentTaskGroupingState(this.getViewKey()) ?? TaskGroupingState.Progress,
            onGroupByChange: this.onGroupByChange,
            showTooltip: true,
            styles: menuIconButtonStyles
        } : undefined;
        const viewModifiersProps = {
            filterMenuButtonProps: {
                currentTaskFilter: currentTaskFilter,
                getUserPhoto: ()=>null,
                onRenderPersona: async (user)=>{
                    const userAC = await this.props.actionCreatorProviders.user();
                    userAC.fetchUsers([
                        user.id
                    ]);
                },
                setTaskFilter: this.setTaskFilter,
                taskFilterOptions: MyTasksFilterOptions,
                styles: menuIconButtonStyles
            },
            overflowContextMenuProps: overflowContextMenuProps,
            groupByMenuButtonProps
        };
        return /*#__PURE__*/ React.createElement(FilterPivotRow, {
            keywordFilterProps: {
                placeholder: Strings.HubActions_FilterByKeyword,
                filterInput: currentlyTypedText.toString(),
                onFilterInputUpdated: this.onUpdateFilterInputValue
            },
            pillPivotProps: pillPivotProps,
            viewModifiersProps: viewModifiersProps
        });
    }
    /**
     * Get filter pivot items for MyTasksPage
     */ getFilterPivotItems() {
        return [
            {
                headerText: getMyTasksPivotString(MyTasksViewPivotType.All),
                itemKey: MyTasksViewPivotType.All,
                pivotElementId: ElementIds.MyTasksAllPivot
            },
            {
                headerText: getMyTasksPivotString(MyTasksViewPivotType.PrivateTasks),
                itemKey: MyTasksViewPivotType.PrivateTasks,
                pivotElementId: ElementIds.MyTasksPrivatePivot
            },
            {
                headerText: getMyTasksPivotString(MyTasksViewPivotType.AssignedToMe),
                itemKey: MyTasksViewPivotType.AssignedToMe,
                pivotElementId: ElementIds.MyTasksAssignedToMePivot
            },
            {
                headerText: getMyTasksPivotString(MyTasksViewPivotType.FlaggedEmails),
                itemKey: MyTasksViewPivotType.FlaggedEmails,
                pivotElementId: ElementIds.MyTasksFlaggedEmailsPivot
            }
        ];
    }
    /**
     * Render the view
     */ renderView() {
        const routeStore = this.props.storeLoader.load(Store.Route);
        if (routeStore == null) {
            return /*#__PURE__*/ React.createElement(React.Fragment, null);
        }
        const routingDetails = extractMyTasksRoutingDetailsFromSubRoute(routeStore.getCurrentSubRoute(), routeStore.getCurrentSubRouteType());
        const subRouteType = routeStore.getCurrentSubRouteType();
        if (subRouteType !== SubRouteType.MyTasks) {
            return /*#__PURE__*/ React.createElement(React.Fragment, null);
        }
        switch(routingDetails?.pivotType){
            case MyTasksViewPivotType.All:
                return this.renderAllUserTasksView();
            case MyTasksViewPivotType.PrivateTasks:
                return this.renderPrivateTasksView();
            case MyTasksViewPivotType.FlaggedEmails:
                return this.renderFlaggedEmailsView();
            case MyTasksViewPivotType.AssignedToMe:
            default:
                return this.renderAssignedToUserTasksView();
        }
    }
    /**
     * Render the all user tasks view
     */ renderAllUserTasksView() {
        const { actionCreatorProviders, storeLoader, dispatcherEventManagement, theme, loggers, configProvider } = this.props;
        const [routeStore, appContextStore, basicPlanStore] = storeLoader.loadAll([
            Store.Route,
            Store.AppContext,
            Store.BasicPlan
        ]);
        const currentViewPivot = getEnumTypeValueForKey(routeStore?.getCurrentSubRoute() ?? [], SubRouteKeys.View, ViewType) ?? ViewType.Grid;
        const userId = appContextStore.getAppContext().userId ?? "";
        const autoFocus = false; // TODO: PBI #9690897 appContextStore.getLastRouteChangeSource() === PlannerRouteChangeSource.Other;
        if (configProvider().flights.EnableMyTasksBoardView && currentViewPivot === ViewType.Board) {
            return /*#__PURE__*/ React.createElement(LazyMyTasksBoardView, {
                isCriticalDataReady: this.isPrivateTasksViewDataReady() || this.isAssignedToMeTasksViewDataReady() || this.isFlaggedEmailsViewDataReady(),
                actionCreatorProviders: actionCreatorProviders,
                storeLoader: storeLoader,
                configProvider: configProvider,
                isSlimView: false,
                viewPartitionKey: userId,
                viewKey: this.getViewKey(),
                dispatcherEventManagement: dispatcherEventManagement,
                theme: theme,
                autoFocus: autoFocus,
                loggers: loggers,
                getTaskData: this.getAllUserTasks,
                showPlanTitleOnCard: true,
                defaultPlanIds: basicPlanStore?.getDefaultPlanId() ? [
                    basicPlanStore.getDefaultPlanId()
                ] : []
            });
        }
        const allUserTasksGrid = /*#__PURE__*/ React.createElement(AllUserTasksGridView, {
            isCriticalDataReady: this.isPrivateTasksViewDataReady() || this.isAssignedToMeTasksViewDataReady() || this.isFlaggedEmailsViewDataReady(),
            actionCreatorProviders: actionCreatorProviders,
            storeLoader: storeLoader,
            configProvider: configProvider,
            isSlimView: false,
            viewPartitionKey: userId,
            viewKey: this.getViewKey(),
            dispatcherEventManagement: dispatcherEventManagement,
            theme: theme,
            autoFocus: autoFocus,
            loggers: loggers,
            getTaskData: this.getAllUserTasks,
            fetchAllPlans: ()=>{},
            enableEnterFooterRowInEditRow: true
        });
        return this.checkForCriticalDataReady() && isEmpty(this.getAllUserTasks()) ? /*#__PURE__*/ React.createElement(React.Fragment, null, allUserTasksGrid, this.renderEmptyStateView(MyTasksViewPivotType.All)) : allUserTasksGrid;
    }
    /**
     * Render the assigned to me tasks view
     */ renderAssignedToUserTasksView() {
        const { actionCreatorProviders, dispatcherEventManagement, theme, loggers, configProvider, storeLoader } = this.props;
        const [routeStore, appContextStore] = storeLoader.loadAll([
            Store.Route,
            Store.AppContext
        ]);
        const currentViewPivot = getEnumTypeValueForKey(routeStore?.getCurrentSubRoute() ?? [], SubRouteKeys.View, ViewType) ?? ViewType.Grid;
        const userId = appContextStore.getAppContext().userId ?? "";
        const autoFocus = false; // TODO: PBI #9690897 appContextStore.getLastRouteChangeSource() === PlannerRouteChangeSource.Other;
        if (configProvider().flights.EnableMyTasksBoardView && currentViewPivot === ViewType.Board) {
            return /*#__PURE__*/ React.createElement(LazyMyTasksBoardView, {
                isCriticalDataReady: this.isAssignedToMeTasksViewDataReady(),
                actionCreatorProviders: actionCreatorProviders,
                storeLoader: storeLoader,
                configProvider: configProvider,
                isSlimView: false,
                viewPartitionKey: userId,
                viewKey: this.getViewKey(),
                dispatcherEventManagement: dispatcherEventManagement,
                theme: theme,
                autoFocus: autoFocus,
                loggers: loggers,
                getTaskData: this.getAssignedToUserTasks,
                showPlanTitleOnCard: true,
                defaultPlanIds: []
            });
        }
        const assignedToUserGrid = /*#__PURE__*/ React.createElement(AssignedToUserGridView, {
            isCriticalDataReady: this.isAssignedToMeTasksViewDataReady(),
            actionCreatorProviders: actionCreatorProviders,
            storeLoader: storeLoader,
            configProvider: configProvider,
            isSlimView: false,
            viewPartitionKey: userId,
            viewKey: this.getViewKey(),
            dispatcherEventManagement: dispatcherEventManagement,
            theme: theme,
            autoFocus: autoFocus,
            loggers: loggers,
            getTaskData: this.getAssignedToUserTasks,
            fetchAllPlans: ()=>{},
            enableEnterFooterRowInEditRow: true
        });
        return this.checkForCriticalDataReady() && isEmpty(this.getAssignedToUserTasks()) ? /*#__PURE__*/ React.createElement(React.Fragment, null, assignedToUserGrid, this.renderEmptyStateView(MyTasksViewPivotType.AssignedToMe)) : assignedToUserGrid;
    }
    /**
     * Render the flagged emails view
     */ renderFlaggedEmailsView() {
        const { actionCreatorProviders, dispatcherEventManagement, theme, loggers, configProvider, storeLoader } = this.props;
        const [routeStore, appContextStore, viewStore, basicPlanStore] = storeLoader.loadAll([
            Store.Route,
            Store.AppContext,
            Store.View,
            Store.BasicPlan
        ]);
        if (basicPlanStore == null || viewStore == null) {
            return /*#__PURE__*/ React.createElement(React.Fragment, null);
        }
        const currentViewPivot = getEnumTypeValueForKey(routeStore?.getCurrentSubRoute() ?? [], SubRouteKeys.View, ViewType) ?? ViewType.Grid;
        const planId = basicPlanStore.getFlaggedEmailPlanId();
        const tasks = planId != null ? this.getFlaggedEmailsTasks() : [];
        /**
         * Render empty state in following conditions-
         * 1. Flagged email plan is not yet provisioned for this user.
         * 2. Flagged email plan is provisioned but it is empty.
         */ if (planId == null || this.checkForCriticalDataReady() && isEmpty(tasks)) {
            return this.renderEmptyStateView(MyTasksViewPivotType.FlaggedEmails);
        }
        const filteredTasks = this.getFilteredTasks(tasks);
        if (filteredTasks.length === 0) {
            return this.renderEmptyStateView(MyTasksViewPivotType.FlaggedEmails, EmptyStateViewType.NoTasksFoundForAppliedFilters);
        }
        const autoFocus = false; // TODO: PBI #9690897 appContextStore.getLastRouteChangeSource() === PlannerRouteChangeSource.Other;
        if (configProvider().flights.EnableMyTasksBoardView && currentViewPivot === ViewType.Board) {
            return /*#__PURE__*/ React.createElement(LazyMyTasksBoardView, {
                isCriticalDataReady: this.isFlaggedEmailsViewDataReady(),
                actionCreatorProviders: actionCreatorProviders,
                storeLoader: storeLoader,
                configProvider: configProvider,
                isSlimView: false,
                viewPartitionKey: planId,
                viewKey: this.getViewKey(),
                dispatcherEventManagement: dispatcherEventManagement,
                theme: theme,
                autoFocus: autoFocus,
                loggers: loggers,
                getTaskData: this.getFlaggedEmailsTasks,
                showPlanTitleOnCard: false,
                defaultPlanIds: []
            });
        }
        return /*#__PURE__*/ React.createElement(MyTasksBasicPlanTasksGridView, {
            key: planId,
            isCriticalDataReady: this.isFlaggedEmailsViewDataReady(),
            disableAddTask: true,
            actionCreatorProviders: actionCreatorProviders,
            storeLoader: storeLoader,
            configProvider: configProvider,
            isSlimView: false,
            viewPartitionKey: planId,
            viewKey: this.getViewKey(),
            dispatcherEventManagement: dispatcherEventManagement,
            theme: theme,
            autoFocus: autoFocus,
            loggers: loggers,
            getTaskData: this.getFlaggedEmailsTasks,
            enableEnterFooterRowInEditRow: true,
            currentUserId: appContextStore.getAppContext().userId ?? ""
        });
    }
    /**
     * Render the private tasks view
     */ renderPrivateTasksView() {
        const { actionCreatorProviders, storeLoader, dispatcherEventManagement, theme, loggers, configProvider } = this.props;
        const [routeStore, appContextStore, basicPlanStore] = storeLoader.loadAll([
            Store.Route,
            Store.AppContext,
            Store.BasicPlan
        ]);
        if (basicPlanStore == null) {
            return /*#__PURE__*/ React.createElement(React.Fragment, null);
        }
        const planId = basicPlanStore.getDefaultPlanId();
        if (planId == null) {
            return /*#__PURE__*/ React.createElement(React.Fragment, null);
        }
        const currentViewPivot = getEnumTypeValueForKey(routeStore?.getCurrentSubRoute() ?? [], SubRouteKeys.View, ViewType) ?? ViewType.Grid;
        const autoFocus = false; // TODO: PBI #9690897 appContextStore.getLastRouteChangeSource() === PlannerRouteChangeSource.Other;
        if (configProvider().flights.EnableMyTasksBoardView && currentViewPivot === ViewType.Board) {
            return /*#__PURE__*/ React.createElement(LazyMyTasksBoardView, {
                isCriticalDataReady: this.isPrivateTasksViewDataReady(),
                actionCreatorProviders: actionCreatorProviders,
                storeLoader: storeLoader,
                configProvider: configProvider,
                isSlimView: false,
                viewPartitionKey: planId,
                viewKey: this.getViewKey(),
                dispatcherEventManagement: dispatcherEventManagement,
                theme: theme,
                autoFocus: autoFocus,
                loggers: loggers,
                getTaskData: this.getPrivateTasks,
                showPlanTitleOnCard: false,
                defaultPlanIds: basicPlanStore?.getDefaultPlanId() ? [
                    basicPlanStore.getDefaultPlanId()
                ] : []
            });
        }
        const privateTasksGrid = /*#__PURE__*/ React.createElement(MyTasksBasicPlanTasksGridView, {
            key: planId,
            isCriticalDataReady: this.isPrivateTasksViewDataReady(),
            actionCreatorProviders: actionCreatorProviders,
            storeLoader: storeLoader,
            configProvider: configProvider,
            isSlimView: false,
            viewPartitionKey: planId,
            viewKey: this.getViewKey(),
            dispatcherEventManagement: dispatcherEventManagement,
            theme: theme,
            autoFocus: autoFocus,
            loggers: loggers,
            getTaskData: this.getPrivateTasks,
            enableEnterFooterRowInEditRow: true,
            currentUserId: appContextStore.getAppContext().userId ?? ""
        });
        return this.checkForCriticalDataReady() && isEmpty(this.getPrivateTasks()) ? /*#__PURE__*/ React.createElement(React.Fragment, null, privateTasksGrid, this.renderEmptyStateView(MyTasksViewPivotType.PrivateTasks)) : privateTasksGrid;
    }
    /**
     * Render Copilot Button
     * */ renderCopilotButton() {
        const { storeLoader, configProvider } = this.props;
        const [appContextStore, basicPermissionStore, tenantStore] = storeLoader.loadAll([
            Store.AppContext,
            Store.BasicPermissions,
            Store.Tenant
        ]);
        const shouldRenderCopilotButton = configProvider().flights.EnableCopilotMyDayMyTasks && appContextStore?.isCopilotEnabled() && basicPermissionStore?.getUserPermissions().canUseCopilot && tenantStore?.getCurrentTenantFeatureSettings().data?.allowPlannerCopilot;
        if (!shouldRenderCopilotButton) {
            return null;
        }
        const enabledFeatures = [
            CopilotFeatureName.ChatQnA,
            CopilotFeatureName.TaskPrioritization
        ];
        this.copilotTelemetryHandler.logEnabledFeatures(enabledFeatures);
        return /*#__PURE__*/ React.createElement(ActionButton, {
            onClick: this.onClickCopilotButton,
            iconProps: copilotIconProps,
            styles: iconButtonStyles
        }, Strings.Copilot_Copilot);
    }
    /**
     * Get task data for the plan tasks view
     */ getMyTasksBasicPlanTasks(viewPartitionKey) {
        const basicTaskStore = this.props.storeLoader.load(Store.BasicTask);
        if (basicTaskStore == null) {
            return [];
        }
        return basicTaskStore.getTasksForPlan(viewPartitionKey).data ?? [];
    }
    /**
     * Render empty state view
     * @param pivotType Pivot type for MyTasks view
     * @param emptyStateViewType Empty state view type
     */ renderEmptyStateView(pivotType, emptyStateViewType) {
        const emptyStateViewProps = {
            emptyStateTitle: this.getEmptyStateTitle(pivotType, emptyStateViewType),
            emptyStateDescription: this.getEmptyStateDescription(pivotType, emptyStateViewType),
            emptyStateSvgSrc: getLazyTasksImage
        };
        return /*#__PURE__*/ React.createElement(React.Suspense, {
            fallback: /*#__PURE__*/ React.createElement(React.Fragment, null)
        }, /*#__PURE__*/ React.createElement(LazyEmptyStateView, emptyStateViewProps));
    }
    /**
     * Get the empty state title based on the current pivot and empty state view type
     */ getEmptyStateTitle(currentFilterPivot = MyTasksViewPivotType.All, emptyStateViewType) {
        switch(currentFilterPivot){
            case MyTasksViewPivotType.PrivateTasks:
                return Strings.EmptyStateView_NoTasksTitlePrivateTasks;
            case MyTasksViewPivotType.AssignedToMe:
                return Strings.EmptyStateView_NoTasksTitleAssignedToMe;
            case MyTasksViewPivotType.FlaggedEmails:
                if (emptyStateViewType === EmptyStateViewType.NoTasksFoundForAppliedFilters) {
                    return Strings.EmptyStateView_NoTasksForAppliedFiltersTitleFlaggedEmails;
                }
                return Strings.EmptyStateView_NoTasksTitleFlaggedEmails;
            case MyTasksViewPivotType.All:
            default:
                return Strings.EmptyStateView_NoTasksTitleMyTasks;
        }
    }
    /**
     * Get the empty state description based on the current pivot and empty state view type
     */ getEmptyStateDescription(currentFilterPivot = MyTasksViewPivotType.All, emptyStateViewType) {
        switch(currentFilterPivot){
            case MyTasksViewPivotType.PrivateTasks:
                return Strings.EmptyStateView_NoTasksDescriptionPrivateTasks;
            case MyTasksViewPivotType.AssignedToMe:
                return Strings.EmptyStateView_NoTasksDescriptionAssignedToMe;
            case MyTasksViewPivotType.FlaggedEmails:
                if (emptyStateViewType === EmptyStateViewType.NoTasksFoundForAppliedFilters) {
                    return Strings.EmptyStateView_NoTasksForAppliedFiltersDescriptionFlaggedEmails;
                }
                return Strings.EmptyStateView_NoTasksDescriptionFlaggedEmails;
            case MyTasksViewPivotType.All:
            default:
                return Strings.EmptyStateView_NoTasksDescriptionMyTasks;
        }
    }
    /**
     * Get the pivots for the header
     */ getViewPivotItems() {
        const routeStore = this.props.storeLoader.load(Store.Route);
        const currentViewPivot = getEnumTypeValueForKey(routeStore?.getCurrentSubRoute() ?? [], SubRouteKeys.View, ViewType) ?? ViewType.Grid;
        const pivots = [];
        pivots.push({
            headerText: Strings.MyPlansHeader_GridViewPivotItemTitle,
            itemIcon: currentViewPivot === ViewType.Grid ? gridIconFilledProps.iconName : gridIconProps.iconName,
            itemKey: ViewType.Grid,
            tooltipText: `${Strings.MyPlansHeader_View} ${Strings.MyPlansHeader_GridViewPivotItemTitle}`,
            pivotElementId: ElementIds.GridViewPivot
        });
        pivots.push({
            headerText: Strings.MyPlansHeader_BoardViewPivotItemTitle,
            itemIcon: currentViewPivot === ViewType.Board ? taskboardIconFilledProps.iconName : taskboardIconProps.iconName,
            itemKey: ViewType.Board,
            tooltipText: `${Strings.MyPlansHeader_View} ${Strings.MyPlansHeader_BoardViewPivotItemTitle}`,
            pivotElementId: ElementIds.BoardViewPivot
        });
        return pivots;
    }
    /**
     * Get the view action creator
     */ async getViewAC() {
        const { actionCreatorProviders } = this.props;
        return await actionCreatorProviders.myTasksView();
    }
    /**
     * Check whether the private tasks view data is ready
     */ isPrivateTasksViewDataReady() {
        const basicPlanStore = this.props.storeLoader.load(Store.BasicPlan);
        const planId = basicPlanStore?.getDefaultPlanId();
        if (!planId) {
            return false;
        }
        return this.isPlanCriticalDataReady(planId);
    }
    /**
     * Check whether the assigned to me tasks view data is ready
     */ isAssignedToMeTasksViewDataReady() {
        const [appContextStore, basicTaskStore] = this.props.storeLoader.loadAll([
            Store.AppContext,
            Store.BasicTask
        ]);
        if (!basicTaskStore) {
            return false;
        }
        const currentUserId = appContextStore.getAppContext().userId;
        if (!currentUserId) {
            return false;
        }
        const storeReadResult = basicTaskStore.getTasksAssignedToUser(currentUserId);
        const isSomeDataLoaded = storeReadResult.data.length > 0;
        const isDataFinalized = !isStatusFetchingOrUnfetched(storeReadResult.status);
        return isSomeDataLoaded || isDataFinalized;
    }
    /**
     * Check whether the flagged emails view data is ready
     */ isFlaggedEmailsViewDataReady() {
        const basicPlanStore = this.props.storeLoader.load(Store.BasicPlan);
        const planId = basicPlanStore?.getFlaggedEmailPlanId();
        if (!planId) {
            return false;
        }
        return this.isPlanCriticalDataReady(planId);
    }
    /**
     * Check if critical data for plan is ready
     */ isPlanCriticalDataReady(planId) {
        const basicTaskStore = this.props.storeLoader.load(Store.BasicTask);
        if (!basicTaskStore) {
            return false;
        }
        const storeReadResult = basicTaskStore.getTasksForPlan(planId);
        const isSomeDataLoaded = storeReadResult.data.length > 0;
        const isDataFinalized = !isStatusFetchingOrUnfetched(storeReadResult.status);
        return isSomeDataLoaded || isDataFinalized;
    }
    /**
     * Get the private tasks view data state
     */ getPrivateTasksViewRenderState() {
        const basicPlanStore = this.props.storeLoader.load(Store.BasicPlan);
        const planId = basicPlanStore ? basicPlanStore.getDefaultPlanId() : null;
        if (!planId) {
            return ViewRenderState.None;
        }
        return this.getPlanDataState(planId);
    }
    /**
     * Get the flagged emails view data state
     */ getFlaggedEmailsViewRenderState() {
        const basicPlanStore = this.props.storeLoader.load(Store.BasicPlan);
        const planId = basicPlanStore?.getFlaggedEmailPlanId();
        if (!planId) {
            return ViewRenderState.None;
        }
        return this.getPlanDataState(planId);
    }
    /**
     * Get the assigned to me tasks view data state
     */ getAssignedToMeViewRenderState() {
        const { configProvider, storeLoader } = this.props;
        const [appContextStore, basicTaskStore, tenantStore, upsellStore, basicPlanStore] = storeLoader.loadAll([
            Store.AppContext,
            Store.BasicTask,
            Store.Tenant,
            Store.Upsell,
            Store.BasicPlan
        ]);
        const currentUserId = appContextStore.getAppContext().userId;
        if (!currentUserId) {
            return ViewRenderState.None;
        }
        // Get critical data from stores
        const storeReadResult = basicTaskStore?.getTasksAssignedToUser(currentUserId);
        const tasks = this.getAllUserTasks();
        const filteredTasks = this.getFilteredTasks(tasks);
        const minimumTasksNeeded = configProvider().settings.performanceSettings.minimumTasksForVisualReadiness;
        // Check if plan data is available for the minimum number of tasks needed for visual readiness or lazy loading of plan details is enabled
        const isPlanDataLoadedOrToBeLazyLoaded = configProvider().flights.EnableLazyLoadingOfPlanDetails || filteredTasks.slice(0, minimumTasksNeeded).every((task)=>this.isPlanDataAvailableForTask(task));
        const isDataFinalized = storeReadResult ? !isStatusFetchingOrUnfetched(storeReadResult.status) : false;
        // Data is considered loaded if the minimum number of tasks required for visual readiness are available,
        // and the plan data for those tasks is loaded when lazy loading of plan details is disabled.
        const isDataLoaded = filteredTasks.length >= minimumTasksNeeded && isPlanDataLoadedOrToBeLazyLoaded;
        // Get non-critical data from stores
        let isBasicPlanDataFetched = true;
        for (const task of tasks){
            const plan = basicPlanStore?.getPlan(task.planId);
            if (!plan || isStatusFetchingOrUnfetched(plan.status)) {
                isBasicPlanDataFetched = false;
                break;
            }
        }
        const isAppContextStoreDataFetched = !isStatusFetchingOrUnfetched(appContextStore.getTodoSettings().status);
        const isTenantStoreDataFetched = tenantStore ? !isStatusFetchingOrUnfetched(tenantStore.getCurrentTenantFeedbackSettings().status) : false;
        const isUpsellStoreDataFetched = upsellStore ? !isStatusFetchingOrUnfetched(upsellStore.getUserProjectSubscriptionStatus().status) : false;
        if (isAppContextStoreDataFetched && isTenantStoreDataFetched && isUpsellStoreDataFetched && isBasicPlanDataFetched && isBasicPlanDataFetched && isDataFinalized) {
            return ViewRenderState.All;
        }
        if (isDataLoaded || isDataFinalized) {
            return ViewRenderState.Critical;
        }
        return ViewRenderState.None;
    }
    /**
     * Get the plan data state
     */ getPlanDataState(planId) {
        const { configProvider, storeLoader } = this.props;
        const basicTaskStore = storeLoader.load(Store.BasicTask);
        if (!basicTaskStore) {
            return ViewRenderState.None;
        }
        const storeReadResult = basicTaskStore.getTasksForPlan(planId);
        const tasks = this.getMyTasksBasicPlanTasks(planId);
        const filteredTasks = this.getFilteredTasks(tasks);
        const isDataFinalized = !isStatusFetchingOrUnfetched(storeReadResult.status);
        const isDataLoaded = filteredTasks.length >= configProvider().settings.performanceSettings.minimumTasksForVisualReadiness;
        return isDataLoaded || isDataFinalized ? ViewRenderState.Critical : ViewRenderState.None;
    }
    /**
     * Gets the list of tasks after applying the filter.
     *
     * @param tasks - The list of tasks to be filtered.
     * @returns The filtered list of tasks.
     */ getFilteredTasks(tasks) {
        const viewStore = this.props.storeLoader.load(Store.View);
        if (!viewStore) {
            return [];
        }
        const currentTaskFilter = viewStore.getCurrentTaskFilter(this.getViewKey());
        const filterExceptions = viewStore.getFilterExceptions();
        const filteredTasks = tasks.filter((task)=>Task.ApplyEntityFilter(task, currentTaskFilter, filterExceptions));
        return filteredTasks;
    }
    /**
     * Checks if the plan data is available for the given task.
     */ isPlanDataAvailableForTask(task) {
        const basicPlanStore = this.props.storeLoader.load(Store.BasicPlan);
        if (!basicPlanStore || !task.planId) {
            return false;
        }
        const plan = basicPlanStore.getPlan(task.planId);
        return !!plan?.data;
    }
    /**
     * Get the view key
     */ getViewKey() {
        const appContextStore = this.props.storeLoader.load(Store.AppContext);
        return getViewKey(TaskAccessContext.AssignedToUser.toString(), appContextStore?.getAppContext().userId ?? "");
    }
    /**
     * Initializes a new instance of the MyTasksPage class
     * @param props Props for the MyTasksPage component
     * @param [context] Context to pass onto the base component
     */ constructor(props, context){
        super(props, context), /** Uno Copilot telemetry handler instance */ this.copilotTelemetryHandler = CopilotTelemetryHandler.getInstance(), /**
     * Called when page detects critical data is ready during "checkForCriticalDataReady" and calls view action creator onCriticalDataFetchCompletion
     * For performance and to avoid component update loops this method executes once per viewPartitionKey on the view component.
     */ this.onPageCriticalDataFetchCompletion = async ()=>{
            const viewAc = await this.getViewAC();
            // CheckForCriticalDataReady is called several times during component lifecycle, tracks dispatched event to make sure it runs once per view partition key
            if (!this.onCriticalDataReadyDispatched) {
                this.onCriticalDataReadyDispatched = true;
                viewAc.onCriticalDataFetchCompletion();
            }
        }, /** Callback to fetch view data on route change */ this.routeChangeFetchDataCallback = async ()=>{
            const { currentFilterPivot } = this.state;
            const viewAC = await this.getViewAC();
            viewAC.fetchViewData(currentFilterPivot, false);
        }, /**
     * Get task data for the all user tasks view
     */ this.getAllUserTasks = ()=>{
            // Assigned to user tasks
            const assignedToUserTasks = this.getAssignedToUserTasks();
            // Private tasks
            const privateTasks = this.getPrivateTasks();
            // Flagged emails
            const flaggedEmailTasks = this.getFlaggedEmailsTasks();
            return [
                ...assignedToUserTasks,
                ...privateTasks,
                ...flaggedEmailTasks
            ];
        }, /**
     * Get task data for the assigned to user tasks view
     */ this.getAssignedToUserTasks = ()=>{
            const { storeLoader } = this.props;
            const [appContextStore, basicTaskStore] = storeLoader.loadAll([
                Store.AppContext,
                Store.BasicTask
            ]);
            if (basicTaskStore == null) {
                return [];
            }
            const userId = appContextStore.getAppContext().userId;
            if (userId == null) {
                return [];
            }
            return basicTaskStore.getTasksAssignedToUser(userId).data ?? [];
        }, /**
     * Get task data for the flagged emails view
     */ this.getFlaggedEmailsTasks = ()=>{
            const { storeLoader } = this.props;
            const basicPlanStore = storeLoader.load(Store.BasicPlan);
            if (basicPlanStore == null) {
                return [];
            }
            const planId = basicPlanStore.getFlaggedEmailPlanId();
            if (planId == null) {
                return [];
            }
            return this.getMyTasksBasicPlanTasks(planId);
        }, /**
     * Get task data for the private view
     */ this.getPrivateTasks = ()=>{
            const { storeLoader } = this.props;
            const basicPlanStore = storeLoader.load(Store.BasicPlan);
            if (basicPlanStore == null) {
                return [];
            }
            const planId = basicPlanStore.getDefaultPlanId();
            if (planId == null) {
                return [];
            }
            return this.getMyTasksBasicPlanTasks(planId);
        }, /**
     * Callback to handle click on trial status button in app header
     */ this.onClickTrialStatusButton = async (evt)=>{
            const { actionCreatorProviders } = this.props;
            const modalActionCreator = await actionCreatorProviders.modal();
            modalActionCreator.openUpsellModalForUser(TryPremiumEntryPoint.Header);
        }, /**
     * Callback to handle click on feedback button in app header
     */ this.onClickFeedback = async ()=>{
            const { actionCreatorProviders } = this.props;
            const ocvActionCreator = await actionCreatorProviders.ocv();
            ocvActionCreator.openInAppFeedback();
        }, /**
     * Callback to handle click on Copilot button in app header
     */ this.onClickCopilotButton = async ()=>{
            const { actionCreatorProviders } = this.props;
            const modalAc = await actionCreatorProviders.modal();
            modalAc.openCopilotSidecarModal();
        }, /**
     * Callback to handle click on about button in app header
     */ this.onClickAbout = async ()=>{
            const { actionCreatorProviders } = this.props;
            const modalAc = await actionCreatorProviders.modal();
            modalAc.openAppInfoModal();
        }, /**
     * Callback to handle click on copy session id button in app header
     */ this.onClickCopySessionId = async ()=>{
            copyToClipboard(this.props.configProvider().sessionMetaData.sessionId);
            const notificationAc = await this.props.actionCreatorProviders.notification();
            notificationAc.enqueueToast("Toasts_CopySessionId");
        }, /**
     * Callback for when the keyword filter input value changes
     * @param filterInput The new filter input value
     */ this.onUpdateFilterInputValue = async (filterInput)=>{
            const { engagementLogger } = this.props.loggers;
            const viewStore = this.props.storeLoader.load(Store.View);
            if (viewStore == null) {
                return;
            }
            const currentTaskFilter = viewStore.getCurrentTaskFilter(this.getViewKey());
            engagementLogger.logEngagement(EngagementTelemetryConstants.TaskFilterMenu, EngagementTelemetryConstants.FilterByKeyword);
            const newText = filterInput ?? "";
            let newFilterSet;
            if (newText === "") {
                newFilterSet = currentTaskFilter.removeFilter("title");
            } else {
                newFilterSet = currentTaskFilter.replaceFilter("title", new Filter("title", [
                    newText
                ]));
            }
            this.setTaskFilter(newFilterSet);
        }, /**
     * Callback to trigger when a pivot is changed.
     * @param pivotKey The new pivot key
     */ this.onViewPivotChange = (pivotKey)=>{
            const { currentFilterPivot } = this.state;
            const { actionCreatorProviders } = this.props;
            const routeAC = actionCreatorProviders.routing();
            routeAC.pushSubRoute([
                currentFilterPivot,
                SubRouteKeys.View,
                pivotKey
            ]);
            if (!this.props.configProvider().flights.EnableCampaignTriggerFromWorker) {
                this.triggerIrisCampaignActions(pivotKey);
            }
        }, /**
     * Callback when the pivot item is changed
     * @param pivot The pivot item that was selected
     */ this.onMyTasksPagePivotItemChanged = (selectedPivotItemKey)=>{
            const { actionCreatorProviders, storeLoader } = this.props;
            const routeStore = storeLoader.load(Store.Route);
            const routeAC = actionCreatorProviders.routing();
            const currentViewPivot = getEnumTypeValueForKey(routeStore?.getCurrentSubRoute() ?? [], SubRouteKeys.View, ViewType) ?? ViewType.Grid;
            this.setState({
                currentFilterPivot: selectedPivotItemKey
            });
            routeAC.pushSubRoute([
                selectedPivotItemKey,
                SubRouteKeys.View,
                currentViewPivot
            ]);
        }, /**
     * Callback to trigger when an GroupBy option is clicked.
     * @param taskGroupState The new task group state
     */ this.onGroupByChange = async (taskGroupState)=>{
            const { actionCreatorProviders } = this.props;
            const viewTransformsAc = await actionCreatorProviders.viewTransforms();
            const viewKey = this.getViewKey();
            viewTransformsAc.setTaskGroupingState(viewKey, taskGroupState);
        }, /**
     * Callback when the task filter is changed
     * @param currentTaskFilter The new task filter
     */ this.setTaskFilter = async (currentTaskFilter, isClearFilter, filterRemoved, announcementTitleStrParams)=>{
            const { actionCreatorProviders } = this.props;
            const viewTransformsAC = await actionCreatorProviders.viewTransforms();
            const viewKey = this.getViewKey();
            viewTransformsAC.setTaskFilter(viewKey, currentTaskFilter, isClearFilter, filterRemoved, announcementTitleStrParams);
        }, /**
     * Callback to open iCalendarDialog
     */ this.openICalendarModal = async ()=>{
            const modalAc = await this.props.actionCreatorProviders.modal();
            modalAc.openICalendarModal(null);
        }, /**
     * Trigger Iris campaign actions based on the current view pivot
     * @param [viewPivot] The pivot view type to trigger Iris campaign actions for
     */ this.triggerIrisCampaignActions = async (viewPivot)=>{
            const { actionCreatorProviders, storeLoader } = this.props;
            const routeStore = storeLoader.load(Store.Route);
            const campaignActionCreator = await actionCreatorProviders.campaign();
            // If viewPivot is defined, it means the pivot is being changed, so we don't need to trigger MyTasksOpen
            const campaignToTrigger = viewPivot ? [] : [
                IrisTriggerAction.MyTasksOpen
            ];
            const currentViewPivot = viewPivot ?? getEnumTypeValueForKey(routeStore.getCurrentSubRoute(), SubRouteKeys.View, ViewType);
            if (currentViewPivot === ViewType.Grid) {
                campaignToTrigger.push(IrisTriggerAction.MyTasksGridOpen);
            } else if (currentViewPivot === ViewType.Board) {
                campaignToTrigger.push(IrisTriggerAction.MyTasksBoardOpen);
            }
            campaignActionCreator.dispatchCampaignTriggerActions(campaignToTrigger);
        };
        this.state = {
            currentFilterPivot: DefaultMyTasksViewPivotType
        };
        // Load translations for the current locale
        loadTranslations(this.props.storeLoader.load(Store.AppContext).getAppContext().locale);
    }
}
